import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useHasChangedShallow } from './useHasChanged';

export const createWithPartialContext =
  <Context extends object>(context: React.Context<Context>) =>
  <Key extends keyof Context>(watchKeys: Key[], componentName?: string) =>
  <ComponentProps extends object>(
    SourceComponent: React.ComponentType<
      React.PropsWithChildren<ComponentProps> & { context: Pick<Context, Key> }
    >
  ): ((props: React.PropsWithChildren<ComponentProps>) => React.ReactElement) => {
    const MemoComponent = React.memo(SourceComponent);
    const Component: (props: React.PropsWithChildren<ComponentProps>) => React.ReactElement = (
      props
    ) => {
      const contextState = React.useContext(context);
      const contextStateRef = React.useRef(contextState);
      const stateChanged = useHasChangedShallow(watchKeys.map((k) => contextState[k]));
      if (stateChanged) {
        contextStateRef.current = contextState;
      }
      // editor thinks it's fine but the compiler still complains about it
      // @ts-ignore
      return <MemoComponent {...props} context={contextStateRef.current} />;
    };
    if (componentName) {
      // @ts-ignore
      MemoComponent.displayName = componentName;
      // @ts-ignore
      Component.displayName = componentName + 'WithPartialContext';
    }
    return Component;
  };

export const withCustomHookValue =
  <ComponentProps extends object, Value extends Record<string, unknown>>(
    getValue: (props: React.PropsWithChildren<Omit<ComponentProps, keyof Value>>) => Value,
    componentName?: string
  ) =>
  (
    SourceComponent: React.ComponentType<React.PropsWithChildren<ComponentProps>>
  ): ((
    props: React.PropsWithChildren<Omit<ComponentProps, keyof Value>>
  ) => React.ReactElement) => {
    const MemoComponent = React.memo(SourceComponent);
    const Component: (
      props: React.PropsWithChildren<Omit<ComponentProps, keyof Value>>
    ) => React.ReactElement = (props) => {
      const valueRef = React.useRef<Value>();
      const value = getValue(props);
      const stateChanged = useHasChangedShallow([value]);
      if (stateChanged || !valueRef.current) {
        valueRef.current = value;
      }
      // editor thinks it's fine but the compiler still complains about it
      // @ts-ignore
      return <MemoComponent {...props} {...value} />;
    };
    if (componentName) {
      // @ts-ignore
      MemoComponent.displayName = componentName;
      // @ts-ignore
      Component.displayName = componentName + 'WithCustomHookValue';
    }
    return Component;
  };

export const withContextValue =
  <Context extends object, ComponentProps extends object, Value extends Record<string, unknown>>(
    context: React.Context<Context>,
    getValue: (
      context: Context,
      props: Omit<ComponentProps, keyof Value>,
      oldValue?: Value
    ) => Value,
    componentName?: string
  ) =>
  (
    SourceComponent: React.ComponentType<React.PropsWithChildren<ComponentProps>>
  ): ((
    props: React.PropsWithChildren<Omit<ComponentProps, keyof Value>>
  ) => React.ReactElement) => {
    const MemoComponent = React.memo(SourceComponent);
    const Component: (
      props: React.PropsWithChildren<Omit<ComponentProps, keyof Value>>
    ) => React.ReactElement = (props) => {
      const contextState = React.useContext(context);
      const valueRef = React.useRef<Value>();
      const value = getValue(contextState, props, valueRef.current);
      const stateChanged = useHasChangedShallow([value]);
      if (stateChanged || !valueRef.current) {
        valueRef.current = value;
      }
      // editor thinks it's fine but the compiler still complains about it
      // @ts-ignore
      return <MemoComponent {...props} {...value} />;
    };
    if (componentName) {
      // @ts-ignore
      MemoComponent.displayName = componentName;
      // @ts-ignore
      Component.displayName = componentName + 'WithContextValue';
    }
    return Component;
  };

export const withFormContextValue =
  <ComponentProps extends object, Value extends Record<string, unknown>>(
    getValue: (
      formValue: Record<string, unknown>,
      props: Omit<ComponentProps, keyof Value>
    ) => Value
  ) =>
  (
    SourceComponent: React.ComponentType<React.PropsWithChildren<ComponentProps>>
  ): ((
    props: React.PropsWithChildren<Omit<ComponentProps, keyof Value>>
  ) => React.ReactElement) => {
    const MemoComponent = React.memo(SourceComponent);
    const Component: (
      props: React.PropsWithChildren<Omit<ComponentProps, keyof Value>>
    ) => React.ReactElement = (props) => {
      const { watch } = useFormContext();
      const value = getValue(watch(), props);
      const valueRef = React.useRef(value);
      const stateChanged = useHasChangedShallow([value]);
      if (stateChanged) {
        valueRef.current = value;
      }
      // editor thinks it's fine but the compiler still complains about it
      // @ts-ignore
      return <MemoComponent {...props} {...value} />;
    };
    return Component;
  };
